
import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, BookOpen, Bell, Search, LogOut,
  Loader, RefreshCw, Download, ChevronRight, Plus, Trash2,
  ExternalLink, AlertTriangle, AlertCircle, FileText, Check, Clock, X, Shield, UserCog, Palette, Zap, Lightbulb
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminDashboard from '../components/AdminDashboard';

// ✅ Import du Chat (fichier externe)
import AIChat from '../components/AIChat';
import ComplianceDigest from '../components/ComplianceDigest'; // [NEW]

// --- COMPOSANTS UI INTÉGRÉS (Pour garantir le design sans dépendance externe) ---

const Badge = ({ children, type }) => {
  const colors = {
    live: "bg-blue-100 text-blue-800 border-blue-200",
    local: "bg-emerald-100 text-emerald-800 border-emerald-200",
    custom: "bg-amber-100 text-amber-800 border-amber-200",
    default: "bg-gray-100 text-gray-800 border-gray-200"
  };
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${colors[type] || colors.default} flex items-center gap-1 w-fit`}>
      {children}
    </span>
  );
};

const Button = ({ children, onClick, variant = 'primary', disabled, className = '' }) => {
  const base = "px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed justify-center";
  const variants = {
    primary: "bg-emerald-700 text-white hover:bg-emerald-800", // Vert (Classique)
    secondary: "bg-blue-700 text-white hover:bg-blue-800",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50 bg-white",
    ghost: "text-gray-600 hover:text-red-600 hover:bg-red-50 bg-transparent"
  };
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${variants[variant] || variants.primary} ${className} `}>
      {children}
    </button>
  );
};

const ThemeSwitcher = ({ currentTheme, onToggleTheme }) => (
  <button
    onClick={onToggleTheme}
    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${currentTheme === 'classic'
      ? 'bg-slate-800 text-emerald-400 border-slate-600 hover:bg-slate-700 shadow-sm'
      : 'bg-slate-800 text-blue-400 border-slate-700 hover:bg-slate-700 shadow-inner'
      } `}
  >
    <Palette className="w-3 h-3" />
    {currentTheme === 'classic' ? 'Clair' : 'Sombre'}
  </button>
);

const Footer = () => {
  const navigate = useNavigate();
  return (
    <footer className="bg-gray-900 text-gray-300 py-10 mt-auto border-t border-gray-800 w-full">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-8 mb-6 text-sm">
          <div>
            <h3 className="text-white font-bold mb-3 text-lg">Réglo-veille</h3>
            <p className="text-gray-200 leading-relaxed">
              Données officielles en provenance de Légifrance et du Code du Travail.
            </p>
          </div>
          <div>
            <h3 className="text-white font-bold mb-3 text-lg">Liens Utiles</h3>
            <ul className="space-y-2">
              <li><button onClick={() => navigate('/help')} className="hover:text-white transition-colors">Centre d'Aide</button></li>
              <li><a href="https://www.legifrance.gouv.fr" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Source Officielle</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-bold mb-3 text-lg">Contact</h3>
            <a href="mailto:contact@quest-ia.fr" className="hover:text-white transition-colors">contact@quest-ia.fr</a>
            <p className="text-xs text-gray-500 mt-2">Une création Quest-IA.fr</p>
          </div>
        </div>
        <div className="border-t border-gray-700 pt-6 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} Quest-IA.fr - Tous droits réservés</p>
        </div>
      </div>
    </footer>
  );
};

// ==================================================================================
// 📊 DASHBOARD PRINCIPAL
// ==================================================================================

const UpgradeModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden border border-amber-200" onClick={e => e.stopPropagation()}>
        <div className="bg-amber-50 p-6 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4 text-amber-600">
            <Zap size={32} className="fill-current" />
          </div>
          <h3 className="text-2xl font-bold text-slate-800 mb-2">Limite atteinte !</h3>
          <p className="text-slate-600 mb-6">
            Vous avez atteint votre quota d'utilisation de l'IA pour ce mois avec votre plan actuel.
          </p>
          <div className="flex flex-col gap-3 w-full">
            <button className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-lg hover:shadow-lg transition-all transform hover:scale-105" onClick={() => { alert("La page d'abonnement sera bientôt disponible."); onClose(); }}>
              Passer au Plan Pro 🚀
            </button>
            <button onClick={onClose} className="text-sm text-slate-500 hover:underline">Non merci</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard({ user, setUser, subscription, trialDaysLeft, logout, API_URL, currentTheme, onToggleTheme }) {
  const navigate = useNavigate();

  // --- STATE ---
  console.log("DASHBOARD RENDER - User:", user);
  const [activeTab, setActiveTab] = useState('flux');
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [regulations, setRegulations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [lastSync, setLastSync] = useState(null);

  // Modales
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showAIChat, setShowAIChat] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [fullArticleText, setFullArticleText] = useState(null);
  const [loadingFullText, setLoadingFullText] = useState(false);

  // ACTION PLAN EXTRACTOR
  const [actionPlan, setActionPlan] = useState(null);
  const [loadingActionPlan, setLoadingActionPlan] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false); // État Modale Upgrade limits

  // --- ADMIN SETTINGS STATE ---
  const [adminSettings, setAdminSettings] = useState({ ai_model: '', ai_api_key: '', ai_base_url: '' });
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const fetchAdminSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.settings) setAdminSettings(data.settings);
    } catch (e) { console.error("Erreur fetch settings:", e); }
  };

  const saveAdminSettings = async () => {
    setIsSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/admin/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ settings: adminSettings })
      });
      if (res.ok) alert("Paramètres enregistrés !");
      else alert("Erreur lors de l'enregistrement");
    } catch (e) { console.error(e); }
    setIsSavingSettings(false);
  };

  const handleDataExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/user/export-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mes-donnees-reglo-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } catch (e) { alert("Erreur lors de l'export des données"); }
  };

  const deleteAccount = async () => {
    if (!window.confirm("🚨 Êtes-vous certain de vouloir supprimer votre compte ? Cette action est irréversible et supprimera toutes vos données personnelles conformément au RGPD.")) return;
    const password = window.prompt("Veuillez saisir votre mot de passe pour confirmer la suppression :");
    if (!password) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/user/account`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ password })
      });
      if (res.ok) {
        alert("Votre compte a été supprimé. Au revoir.");
        logout();
      } else {
        const d = await res.json();
        alert(d.error || "Erreur lors de la suppression");
      }
    } catch (e) { alert("Erreur technique"); }
  };

  const fetchActionPlan = async (article) => {
    setLoadingActionPlan(true);
    setActionPlan(null); // Reset previous plan
    try {
      const token = localStorage.getItem('token');
      // Use article text or description fallback
      const textToAnalyze = article.text || article.description || article.summary || "";

      // [NEW] Call the new Digest endpoint
      const res = await fetch(`${API_URL}/ai/generate-digest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          texte: textToAnalyze,
          context: `Secteur: ${profileData.activity_sector}, Taille: ${profileData.team_size}, Dept: ${profileData.department}`,
          id: article.id // Pass ID to let backend fetch full text if needed
        })
      });

      if (res.status === 403) {
        const errorData = await res.json();
        if (errorData.code === 'LIMIT_REACHED') {
          setShowUpgradeModal(true);
          setLoadingActionPlan(false);
          return;
        }
      }

      if (!res.ok) throw new Error("Erreur extraction");

      const data = await res.json();
      setActionPlan(data);

    } catch (e) {
      console.error("Erreur Action Plan:", e);
      alert("Impossible de générer le plan d'action.");
    }
    setLoadingActionPlan(false);
  };

  // Profil
  const [profileData, setProfileData] = useState({ activity_sector: '', has_premises: false, team_size: '', department: '', naf_code: '', naf_label: '' });
  const [nafDivisions, setNafDivisions] = useState([]);
  const [nafResults, setNafResults] = useState([]);
  const [showNafDropdown, setShowNafDropdown] = useState(false);

  // Custom Regs
  const [newRegTitle, setNewRegTitle] = useState('');
  const [newRegDesc, setNewRegDesc] = useState('');

  const [editingRegId, setEditingRegId] = useState(null);
  const [analyzingId, setAnalyzingId] = useState(null);

  // Pagination simple
  const [itemsToShow, setItemsToShow] = useState(5);
  const ITEMS_PER_PAGE = 5;

  // --- DESIGN SYSTEM ---
  const isClassic = currentTheme === 'classic';

  const themeStyles = {
    container: isClassic ? "bg-gray-50" : "bg-slate-950",
    card: isClassic
      ? "bg-white rounded-xl shadow-sm border border-gray-200"
      : "bg-slate-900 rounded-2xl shadow-xl shadow-black/20 border border-slate-800 text-slate-200 hover:shadow-2xl hover:shadow-black/40 hover:-translate-y-1 transition-all duration-300",
    header: isClassic
      ? "bg-white text-slate-900 border-b border-gray-200 shadow-sm"
      : "bg-slate-950 border-b border-slate-800 text-slate-100 shadow-sm sticky top-0 z-20",
    textPrimary: isClassic ? "text-black" : "text-white",
    textSecondary: isClassic ? "text-black" : "text-white",
    accent: isClassic ? "text-emerald-950" : "text-blue-300",
    button: {
      primary: isClassic
        ? "bg-emerald-900 hover:bg-emerald-950 text-white shadow-sm"
        : "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20 rounded-xl",
      secondary: isClassic
        ? "bg-gray-200 hover:bg-gray-300 text-black font-extrabold"
        : "bg-slate-800 hover:bg-slate-700 text-blue-300 border border-slate-700 rounded-xl"
    },
    tab: {
      active: isClassic
        ? "text-black border-b-2 border-emerald-900 font-extrabold"
        : "bg-blue-600 text-white rounded-xl px-4 shadow-sm",
      inactive: isClassic
        ? "text-black font-extrabold"
        : "text-black hover:text-slate-700 px-4"
    }
  };

  const accentText = themeStyles.accent;
  const activeTabClass = themeStyles.tab.active;
  const actionButtonVariant = isClassic ? 'primary' : 'secondary';

  const themesList = [
    { id: 'duree_travail', label: 'Durée du travail', icon: '⏰' },
    { id: 'sante_securite', label: 'Santé & Sécurité', icon: '🏥' },
    { id: 'remuneration', label: 'Salaire & Rémunération', icon: '💰' },
    { id: 'conges', label: 'Congés & Absences', icon: '🏖️' },
    { id: 'embauche', label: 'Embauche & Contrat', icon: '📋' },
    { id: 'contrat', label: 'Contrat de travail', icon: '📝' },
    { id: 'rupture', label: 'Rupture du contrat', icon: '❌' },
    { id: 'discrimination', label: 'Égalité & Non-discrim.', icon: '⚖️' },
    { id: 'harcelement', label: 'Harcèlement', icon: '🚫' },
    { id: 'teletravail', label: 'Télétravail', icon: '💻' },
    { id: 'formation', label: 'Formation', icon: '📚' },
    { id: 'representation', label: 'Représentation', icon: '👥' },
  ];

  const themeIdToApiKeyword = {
    duree_travail: "durée du travail", sante_securite: "santé sécurité", remuneration: "salaire rémunération",
    conges: "congés absences", embauche: "embauche contrat", contrat: "contrat de travail",
    rupture: "rupture du contrat", discrimination: "egalité non discrimination", harcelement: "harcèlement",
    teletravail: "télétravail", formation: "formation compétences", representation: "représentation du personnel"
  };

  // --- LOGIQUE METIER ---

  const loadUserProfile = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/user/profile`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.user) {
        // [NEW] Mise à jour du user global pour récupérer le rôle admin si changé
        if (setUser) {
          setUser(prev => ({ ...prev, ...data.user }));
        }

        const naf = data.user.naf_code;
        setProfileData({
          activity_sector: data.user.activity_sector || '',
          has_premises: data.user.has_premises || false,
          team_size: data.user.team_size || '',
          department: data.user.department || '',
          naf_code: naf || '',
          naf_label: naf && nafDivisions.length ? (() => {
            const found = nafDivisions.find(d => d.code === String(naf).slice(0, 2));
            return found ? `${found.code} — ${found.label}` : naf;
          })() : (naf || '')
        });
      }
    } catch (e) { console.error(e); }
  }, [API_URL, nafDivisions]);

  const updateProfile = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/user/profile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(profileData)
      });
      setShowProfileModal(false);
      alert("Profil mis à jour !");
    } catch (e) { alert("Erreur mise à jour"); }
    setIsLoading(false);
  };

  const loadUserThemes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_URL}/user-themes`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (data.themes) setSelectedThemes(data.themes);
    } catch (err) { console.error(err); }
  }, [API_URL]);

  const fetchRegulations = useCallback(async (themesToFetch = selectedThemes) => {
    if (themesToFetch.length === 0) return;
    setIsSyncing(true); setApiError(null);

    try {
      const token = localStorage.getItem('token');

      // 1. Appel AGGRÉGÉ pour le LIVE (Légifrance)
      // On prépare la liste des mots-clés
      const keywords = themesToFetch.map(id => themeIdToApiKeyword[id] || id);

      const livePromise = fetch(`${API_URL}/legifrance/search-aggregated`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ themes: keywords })
      }).then(res => res.ok ? res.json() : { results: [] }).then(data => data.results || []).catch(() => []);

      // 2. Appel LOCAL (On garde la boucle pour l'instant car moins coûteux, ou on pourrait faire pareil)
      // Pour simplifier et ne pas casser l'existant local, on garde la boucle Promise.all juste pour le local
      const localPromises = themesToFetch.map(themeId => {
        const keyword = themeIdToApiKeyword[themeId] || themeId;
        return fetch(`${API_URL}/regulations?theme=${encodeURIComponent(keyword)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(res => res.ok ? res.json() : { regulations: [] }).then(data => {
          return (data.regulations || []).map(item => ({ ...item, type: 'local', sourceLabel: 'Base Locale' }));
        }).catch(() => []);
      });

      // 3. Custom
      const customReq = fetch(`${API_URL}/custom-regulations`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json()).then(d => d.regulations || []);

      // Exécution parallèle : [LiveAggregated, ...LocalRequests, Custom]
      const [liveResults, ...otherResults] = await Promise.all([livePromise, ...localPromises, customReq]);

      // Aplatir tous les résultats locaux + custom
      const localAndCustomFlat = otherResults.flat();

      // Fusionner tout
      const allRegulations = [...localAndCustomFlat, ...liveResults];

      // Tri et Unicité
      const sorted = allRegulations.sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at));

      // Dédoublonnage sur CID (prioritaire) ou ID, ET sur la Description pour éviter le spam visuel (Demande utilisateur)
      const unique = sorted.filter((v, i, a) =>
        a.findIndex(t =>
          ((t.cid && t.cid === v.cid) || (t.id === v.id)) // Même ID
          || (t.description === v.description && t.source !== 'Custom') // OU Même description (sauf règles custom)
        ) === i
      );

      setRegulations(unique);
      setLastSync(new Date());
    } catch (err) { setApiError('Erreur de chargement des données'); }
    setIsSyncing(false);
  }, [selectedThemes, API_URL]);

  const analyzeArticle = async (article) => {
    if (article.ai_summary || analyzingId) return;
    setAnalyzingId(article.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/ai/analyze-regulation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ titre: article.titre, texte: article.description, id: article.id })
      });

      if (res.status === 403) {
        const errorData = await res.json();
        if (errorData.code === 'LIMIT_REACHED') {
          setAnalyzingId(null); // Ensure button unlocks immediately
          setShowUpgradeModal(true);
          return;
        }
      }

      const analysis = await res.json();
      setRegulations(prev => prev.map(r => r.id === article.id ? { ...r, ai_summary: analysis.summary, impact_level: analysis.level } : r));
    } catch (e) {
      console.error(e);
      setAnalyzingId(null);
    }
  };

  const addCustomReg = async () => {
    if (!newRegTitle) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingRegId ? `${API_URL}/custom-regulations/${editingRegId}` : `${API_URL}/custom-regulations`;
      const method = editingRegId ? 'PUT' : 'POST';
      await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ titre: newRegTitle, description: newRegDesc, priority: 'normal', actions: [] })
      });
      setNewRegTitle(''); setNewRegDesc(''); setEditingRegId(null); fetchRegulations();
    } catch (err) { setApiError('Erreur sauvegarde'); }
    setIsLoading(false);
  };

  const generatePDF = async () => {
    if (regulations.length === 0) return;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const cleanRegs = regulations.map(r => ({
        titre: r.titre, description: r.description || r.summary || '', source: r.source, date: r.date, actions: r.actions || []
      }));
      const response = await fetch(`${API_URL}/reports/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ userId: user.id, theme: selectedThemes.join(', '), regulations: cleanRegs })
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.details || errData.error || 'Erreur PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'rapport.pdf';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (e) {
      console.error("❌ [FRONTEND PDF ERROR]", e);
      setApiError('Erreur: ' + e.message);
    }
    setIsLoading(false);
  };

  const loadFullArticleText = async (articleId) => {
    if (!articleId) return;
    setLoadingFullText(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/legifrance/get-article?id=${articleId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data && data.article && data.article.text) {
        setFullArticleText(data.article.text);
      } else if (data && data.description) {
        setFullArticleText(data.description);
      } else {
        setFullArticleText("<p>Texte complet non disponible via l'API Légifrance.</p>");
      }
    } catch (error) {
      console.error('Erreur chargement texte:', error);
      setFullArticleText("<p>Erreur lors du chargement du texte complet.</p>");
    }
    setLoadingFullText(false);
  };

  const toggleTheme = async (themeId) => {
    let newThemes;
    if (selectedThemes.includes(themeId)) {
      newThemes = []; // Toggle off if clicked again
    } else {
      newThemes = [themeId]; // Only one theme at a time
    }

    setSelectedThemes(newThemes);
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/user-themes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ themes: newThemes })
    });
  };

  // INITIALISATION
  useEffect(() => {
    if (user) {
      loadUserThemes();
      loadUserProfile();
      if (user.role === 'admin') fetchAdminSettings();
    }
  }, [user, loadUserThemes, loadUserProfile]);

  // Chargement du référentiel NAF/APE (autocomplétion métier)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || nafDivisions.length > 0) return;
    fetch(`${API_URL}/naf/divisions`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.results) setNafDivisions(d.results); })
      .catch(() => {});
  }, [API_URL, nafDivisions.length]);

  useEffect(() => {
    if (selectedThemes.length > 0) {
      fetchRegulations();
      setItemsToShow(ITEMS_PER_PAGE); // Reset à 5 items
    }
  }, [selectedThemes]);

  const filteredRegs = regulations.filter(r => r.titre?.toLowerCase().includes(searchTerm.toLowerCase()) || r.description?.toLowerCase().includes(searchTerm.toLowerCase()));
  const displayedRegs = filteredRegs.slice(0, itemsToShow);
  const hasMore = filteredRegs.length > itemsToShow;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans transition-colors duration-300 relative">

      {/* HEADER */}
      <div className={`${themeStyles.header} w-full transition-all duration-300`}>
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              {/* Logo Cliquable */}
              <button onClick={() => navigate('/')} className={`text-2xl font-bold flex items-center gap-2 hover:opacity-80 transition-colors ${themeStyles.accent}`}>
                <img src="/France.png" alt="Logo" className="w-8 h-8 object-contain bg-white rounded-full p-1" /> Réglo-veille
              </button>
              <div className={`flex items-center gap-4 text-sm mt-2 ${themeStyles.textSecondary}`}>
                {subscription?.status === 'trial' && <span className="bg-orange-600 text-white px-2 py-0.5 rounded text-xs">Essai {trialDaysLeft}j</span>}
                <span className="text-xs font-medium">{user?.email}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <ThemeSwitcher currentTheme={currentTheme} onToggleTheme={onToggleTheme} />
              <button onClick={() => setShowProfileModal(true)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${themeStyles.button.secondary} ${!isClassic && 'rounded-xl shadow-sm'}`}>
                <UserCog size={16} /> Profil
              </button>
              <button onClick={logout} className={`flex items-center gap-1 px-3 py-2 text-sm transition-colors ${isClassic ? 'bg-red-600 text-white rounded-lg hover:bg-red-700' : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 rounded-xl'}`}>
                <LogOut size={16} /> <span className="hidden sm:inline">Déco</span>
              </button>
            </div>
          </div>

          <div className={`flex items-center gap-2 mt-4 pt-4 border-t ${isClassic ? 'border-gray-200' : 'border-slate-100'}`}>


            <div className="ml-auto flex gap-2">
              <input
                type="text"
                placeholder="Filtrer..."
                className={`${isClassic ? 'bg-gray-100 border-gray-300 text-black font-bold focus:bg-white focus:ring-2 focus:ring-emerald-500' : 'bg-slate-900 border-slate-700 text-slate-100 focus:bg-slate-800 focus:ring-2 focus:ring-blue-500'} border rounded px-3 py-1 text-sm outline-none transition-all`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button
                onClick={() => fetchRegulations()}
                disabled={isSyncing || selectedThemes.length === 0}
                className={`${themeStyles.button.secondary} px-4 py-1.5 text-sm font-medium h-auto ${isSyncing ? 'opacity-50' : ''}`}
              >
                {isSyncing ? '...' : 'Sync'}
              </button>
              <button
                onClick={generatePDF}
                disabled={regulations.length === 0 || isLoading}
                className={`${themeStyles.button.primary} px-4 py-1.5 text-sm font-medium flex items-center gap-2 h-auto`}
              >
                {isLoading ? <Loader size={14} className="animate-spin" /> : <Download size={14} />} PDF
              </button>
            </div>
          </div>
          {apiError && <div className="mt-2 text-red-500 text-xs flex items-center gap-2"><AlertCircle size={14} /> {apiError}</div>}
        </div>
      </div>

      {/* CONTENU PRINCIPAL */}
      <div className={`flex-grow w-full p-4 flex flex-col transition-colors duration-300 ${themeStyles.container}`}>
        <div className="max-w-6xl mx-auto w-full">
          {/* Onglets */}
          <div className={`flex gap-4 mb-8 ${isClassic ? 'border-b border-gray-200 pb-1' : 'bg-white/50 p-1.5 rounded-full w-fit backdrop-blur-sm shadow-sm'}`}>
            <button onClick={() => setActiveTab('flux')} className={`py-2 text-sm font-bold uppercase tracking-wide transition-all ${activeTab === 'flux' ? themeStyles.tab.active : themeStyles.tab.inactive}`}>Flux</button>
            <button onClick={() => setActiveTab('custom')} className={`py-2 text-sm font-bold uppercase tracking-wide transition-all ${activeTab === 'custom' ? themeStyles.tab.active : themeStyles.tab.inactive}`}>Règles</button>
            <button onClick={() => setShowAIChat(true)} className={`py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${activeTab === 'chat' ? themeStyles.tab.active : themeStyles.tab.inactive}`}>💬 Assistant IA</button>
            {user?.role === 'admin' && (
              <button onClick={() => setActiveTab('admin')} className={`py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${activeTab === 'admin' ? themeStyles.tab.active : themeStyles.tab.inactive}`}>⚙️ Admin</button>
            )}
          </div>

          {/* VUE FLUX */}
          {activeTab === 'flux' && (
            <>
              <div className="flex items-center gap-3 mb-5 text-xs text-slate-500 flex-wrap">
                <span className="font-bold uppercase tracking-wide">Impact :</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span> Rouge</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block"></span> Jaune</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span> Vert</span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {themesList.map(t => (
                  <button key={t.id} onClick={() => toggleTheme(t.id)} className={`p-3 text-left text-sm transition-all duration-200 ${selectedThemes.includes(t.id) ? (isClassic ? 'bg-slate-800 text-white border-slate-800' : 'bg-blue-600 text-white shadow-lg shadow-blue-500/20 transform scale-[1.02]') : (isClassic ? 'bg-white hover:bg-gray-50 text-black font-bold' : 'bg-slate-800 hover:bg-slate-700 text-slate-300')} ${isClassic ? 'rounded border' : 'rounded-2xl border border-slate-700/50 shadow-sm'}`}>
                    <span className="mr-2">{t.icon}</span> {t.label}
                  </button>
                ))}
              </div>

              {filteredRegs.length === 0 && !isLoading ? (
                <div className={`p-12 text-center ${themeStyles.card}`}>
                  <BookOpen className={`w-12 h-12 mx-auto mb-4 ${isClassic ? 'text-gray-800' : 'text-blue-200'}`} />
                  {selectedThemes.length === 0 ? (
                    <>
                      <p className={themeStyles.textSecondary}>Sélectionnez un thème pour afficher les textes.</p>
                      <p className="text-xs mt-2 text-slate-400">Cliquez sur un thème ci-dessus pour lancer la veille.</p>
                    </>
                  ) : searchTerm ? (
                    <>
                      <p className={themeStyles.textSecondary}>Aucun résultat pour « {searchTerm} ».</p>
                      <p className="text-xs mt-2 text-slate-400">Essayez un autre mot-clé ou cliquez sur "Sync" pour recharger.</p>
                    </>
                  ) : (
                    <>
                      <p className={themeStyles.textSecondary}>Aucun texte pour ce thème pour le moment.</p>
                      <p className="text-xs mt-2 text-slate-400">Cliquez sur "Sync" pour récupérer les dernières données.</p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {displayedRegs.map((reg, idx) => (
                      <div key={idx} onClick={() => setSelectedArticle(reg)} className={`${themeStyles.card} p-6 relative group cursor-pointer h-full flex flex-col`}>
                        {/* INDICATEUR D'IMPACT (Bande latérale ou point) */}
                        {isClassic && (
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${reg.impact_level === 'ROUGE' ? 'bg-red-500' : reg.impact_level === 'JAUNE' ? 'bg-yellow-500' : 'bg-emerald-500'} rounded-l-xl`} />
                        )}

                        {/* FEU TRICOLORE (Badge) */}
                        {reg.impact_level && (
                          <span className={`absolute top-4 right-4 text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-sm ${reg.impact_level === 'ROUGE' ? 'bg-red-50 text-red-600' : reg.impact_level === 'JAUNE' ? 'bg-yellow-50 text-yellow-600' : 'bg-emerald-50 text-emerald-600'}`}>
                            <Zap size={10} className="inline mr-1" /> {reg.impact_level}
                          </span>
                        )}

                        <div className={`mb-4 text-xs font-medium flex justify-between pr-16 items-center ${isClassic ? 'text-gray-800' : 'text-gray-300'}`}>
                          <span className={`${isClassic ? 'bg-gray-200 text-gray-900 border border-gray-300' : 'bg-slate-700 text-slate-200 border border-slate-600'} px-2 py-1 rounded font-bold uppercase tracking-wider`}>{reg.source || 'Légifrance'}</span>
                          <span>{reg.date ? new Date(reg.date).toLocaleDateString() : ''}</span>
                        </div>

                        <h3 className={`font-bold text-lg mb-4 line-clamp-2 leading-tight ${themeStyles.textPrimary} group-hover:${themeStyles.accent} transition-colors min-h-[3.5rem]`}>{reg.titre}</h3>

                        {/* RESUME IA */}
                        <div className={`text-sm mb-6 flex-grow ${themeStyles.textSecondary}`}>
                          {reg.ai_summary ? (
                            <div className={`${isClassic ? 'bg-blue-50 border-blue-100 border' : 'bg-blue-900/40 border border-blue-800/50'} rounded-lg overflow-hidden`}>
                              {/* Header Résumé */}
                              <div className={`p-3 border-b ${isClassic ? 'bg-blue-100/50 border-blue-100' : 'bg-blue-900/60 border-blue-800/50'}`}>
                                <strong className={`flex items-center gap-1 ${isClassic ? 'text-blue-900' : 'text-blue-200'} text-xs uppercase tracking-wide`}>
                                  <Lightbulb size={12} /> L'essentiel pour vous
                                </strong>
                              </div>

                              <div className="p-3">
                                {/* SUMMARY */}
                                <p className={`text-sm mb-3 ${isClassic ? 'text-blue-900' : 'text-slate-200'}`}>
                                  {typeof reg.ai_summary === 'object' ? reg.ai_summary.summary : reg.ai_summary}
                                </p>

                                {/* ACTIONS CHECKLIST */}
                                {typeof reg.ai_summary === 'object' && reg.ai_summary.actions && reg.ai_summary.actions.length > 0 && (
                                  <div className="mb-3">
                                    <h4 className="text-xs font-bold text-emerald-500 uppercase mb-1 flex items-center gap-1"><Check size={10} /> Actions requises</h4>
                                    <ul className="space-y-1">
                                      {reg.ai_summary.actions.map((action, i) => (
                                        <li key={i} className={`flex items-start gap-2 text-xs ${isClassic ? 'text-slate-700' : 'text-slate-300'}`}>
                                          <input type="checkbox" className="mt-0.5" />
                                          <span>{action}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {/* RISK */}
                                {typeof reg.ai_summary === 'object' && reg.ai_summary.risk && (
                                  <div className={`flex items-start gap-2 text-xs p-2 rounded ${isClassic ? 'text-red-600 bg-red-50' : 'text-red-300 bg-red-900/30'}`}>
                                    <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                                    <span><strong>Risque : </strong>{reg.ai_summary.risk}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`${isClassic ? 'text-black' : 'text-gray-200'} font-medium line-clamp-6 prose prose-sm max-w-none leading-relaxed`}
                              dangerouslySetInnerHTML={{ __html: reg.description || "Texte officiel (Cliquez sur l'éclair pour analyser)." }}
                            />
                          )}

                        </div >

                        <div className="mt-auto pt-4 border-t border-dashed border-gray-100 flex justify-between items-center">
                          <span className={`${themeStyles.accent} text-sm font-bold flex items-center gap-1 group-hover:translate-x-1 transition-transform`}>Lire <ChevronRight size={14} /></span>
                          {!reg.ai_summary && (
                            <button
                              onClick={(e) => { e.stopPropagation(); analyzeArticle(reg); }}
                              disabled={analyzingId === reg.id}
                              className={`text-xs px-3 py-1.5 rounded-full flex gap-1 disabled:opacity-70 disabled:cursor-wait transition-colors ${isClassic ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}
                            >
                              {analyzingId === reg.id ? <Loader size={12} className="animate-spin" /> : '⚡'}
                              {analyzingId === reg.id ? 'Analyse...' : 'Simplifier'}
                            </button>
                          )}
                        </div>
                      </div >
                    ))}
                  </div >

                  {/* Bouton Voir Plus */}
                  {
                    hasMore && (
                      <div className="flex justify-center mt-8">
                        <button
                          onClick={() => setItemsToShow(prev => prev + ITEMS_PER_PAGE)}
                          className={`${themeStyles.button.primary} px-6 py-3 font-bold transition-all flex items-center gap-2 ${isClassic ? 'rounded-lg' : 'rounded-full hover:shadow-xl'}`}
                        >
                          <ChevronRight size={18} />
                          Voir plus ({filteredRegs.length - itemsToShow} résultats restants)
                        </button>
                      </div>
                    )
                  }
                </>
              )}
            </>
          )}
          {/* VUE CUSTOM */}
          {
            activeTab === 'custom' && (
              <div className={`${themeStyles.card} p-8 max-w-3xl mx-auto w-full`}>
                <h3 className={`font-bold text-lg mb-6 ${themeStyles.accent}`}>Ajouter une règle interne</h3>
                <input className="w-full mb-4 p-3 border rounded bg-slate-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-blue-100 text-black font-bold" placeholder="Titre" value={newRegTitle} onChange={e => setNewRegTitle(e.target.value)} />
                <textarea className="w-full mb-4 p-3 border rounded h-32 bg-slate-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-blue-100 text-black font-medium" placeholder="Détails..." value={newRegDesc} onChange={e => setNewRegDesc(e.target.value)} />
                <div className="flex justify-end">
                  <button onClick={addCustomReg} disabled={isLoading} className={`${themeStyles.button.primary} px-6 py-2 rounded-lg font-bold`}>Enregistrer</button>
                </div>
              </div>
            )
          }

          {
            activeTab === 'admin' && user?.role === 'admin' && (
              <div className={`${themeStyles.card} p-4 sm:p-8 max-w-5xl mx-auto w-full`}>
                <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${themeStyles.accent}`}><Shield /> Administration Système</h2>
                <AdminDashboard themeStyles={themeStyles} API_URL={API_URL} token={localStorage.getItem('token')} />
              </div>
            )
          }
        </div >
      </div >

      {/* FOOTER */}
      < Footer />

      {/* MODALE LECTURE */}
      {
        selectedArticle && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setSelectedArticle(null); setFullArticleText(null); }}>
            <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-1 rounded uppercase">{selectedArticle.source || 'Légifrance'}</span>
                    {selectedArticle.date && <span className="text-xs text-gray-500">{new Date(selectedArticle.date).toLocaleDateString()}</span>}
                  </div>
                  <h2 className="font-bold text-xl text-slate-800">{selectedArticle.titre}</h2>
                </div>
                <button onClick={() => { setSelectedArticle(null); setFullArticleText(null); }} aria-label="Fermer la lecture" className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X size={24} /></button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">

                {selectedArticle.ai_summary && (
                  <div className="bg-blue-50 p-4 mb-6 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2 text-blue-900 font-bold">
                      <Lightbulb size={16} /> Résumé IA
                    </div>
                    <p className="text-blue-900 text-sm">{selectedArticle.ai_summary}</p>
                  </div>
                )}

                {/* --- COMPLIANCE DIGEST UI (REPLACES OLD ACTION PLAN) --- */}
                {loadingActionPlan ? (
                  <div className="flex flex-col items-center justify-center p-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
                    <Loader className="animate-spin text-emerald-600 mb-4" size={48} />
                    <p className="text-slate-600 font-medium">Analyse et Génération du Rapport en cours...</p>
                    <p className="text-xs text-slate-400 mt-2">Cela peut prendre quelques secondes (IA)</p>
                  </div>
                ) : (
                  <ComplianceDigest digest={actionPlan} />
                )}


                {fullArticleText ? (
                  <div className="prose max-w-none text-black font-medium leading-relaxed" dangerouslySetInnerHTML={{ __html: fullArticleText }} />
                ) : (
                  <div className="text-black text-sm">
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4" dangerouslySetInnerHTML={{ __html: selectedArticle.description || '<p class="italic">Aucune description disponible.</p>' }} />

                    {selectedArticle.type !== 'custom' && selectedArticle.id && (
                      <button
                        onClick={() => loadFullArticleText(selectedArticle.id)}
                        disabled={loadingFullText}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50"
                      >
                        {loadingFullText ? <Loader size={16} className="animate-spin" /> : <BookOpen size={16} />}
                        {loadingFullText ? 'Chargement...' : 'Lire le texte complet'}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="p-4 border-t bg-gray-50 flex justify-between gap-2 rounded-b-xl items-center">
                {/* LEFT: ACTION PLAN BUTTON */}
                <button
                  onClick={() => fetchActionPlan(selectedArticle)}
                  disabled={loadingActionPlan || !!actionPlan}
                  className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50 shadow-sm ${actionPlan ? 'bg-amber-500 cursor-default' : 'bg-amber-600 hover:bg-amber-700'}`}
                >
                  {loadingActionPlan ? <Loader size={16} className="animate-spin" /> : <Zap size={16} />}
                  {loadingActionPlan ? 'Analyse en cours...' : actionPlan ? 'Plan d\'Action Généré' : 'Générer Plan d\'Action'}
                </button>

                {/* RIGHT: CLOSE & LINKS */}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setSelectedArticle(null); setActionPlan(null); }} className="px-4 py-2 border rounded hover:bg-gray-100">Fermer</Button>
                  {selectedArticle.type !== 'custom' && <a href={`https://www.legifrance.gouv.fr/codes/article_lc/${selectedArticle.cid || selectedArticle.id}`} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-2">Voir sur Légifrance <ExternalLink size={14} /></a>}
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* MODALE PROFIL */}
      {
        showProfileModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowProfileModal(false)}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
              <h2 className={`text-xl font-bold mb-6 flex items-center gap-2 ${accentText}`}><UserCog /> Mon Profil Métier</h2>
              <div className="space-y-4">
                <div><label className="block text-sm font-bold mb-1">Activité Principale</label><select className="w-full p-2 border rounded" value={profileData.activity_sector} onChange={e => setProfileData({ ...profileData, activity_sector: e.target.value })}><option>Restauration</option><option>BTP</option><option>Commerce</option><option>Services</option><option>Santé</option></select></div>
                <div className="relative">
                  <label className="block text-sm font-bold mb-1">Code NAF / APE <span className="text-emerald-600 font-medium">(activité principale)</span></label>
                  <input
                    type="text"
                    value={profileData.naf_label}
                    placeholder="Ex. : Restauration, Bâtiment, Coiffure, Commerce…"
                    onChange={e => {
                      const q = e.target.value;
                      setProfileData({ ...profileData, naf_label: q, naf_code: '' });
                      setNafResults(nafDivisions
                        .filter(d => d.label.toLowerCase().includes(q.toLowerCase()) || d.code.includes(q))
                        .slice(0, 8));
                      setShowNafDropdown(true);
                    }}
                    onFocus={() => setShowNafDropdown(true)}
                    onBlur={() => setTimeout(() => setShowNafDropdown(false), 150)}
                    className="w-full p-2 border rounded bg-white text-black outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  {showNafDropdown && nafResults.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full border rounded-lg shadow-lg bg-white max-h-48 overflow-y-auto">
                      {nafResults.map(r => (
                        <button
                          type="button"
                          key={r.code}
                          onMouseDown={e => {
                            e.preventDefault();
                            setProfileData({ ...profileData, naf_code: r.code, naf_label: `${r.code} — ${r.label}` });
                            setShowNafDropdown(false);
                          }}
                          className="block w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 text-slate-700"
                        >
                          <span className="font-bold text-emerald-700">{r.code}</span> <span className="text-slate-600">{r.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 mt-1">Guide nos alertes : ne vous notifions que les textes qui touchent votre activité.</p>
                </div>

                <div><label className="block text-sm font-bold mb-1">Effectif</label><select className="w-full p-2 border rounded" value={profileData.team_size} onChange={e => setProfileData({ ...profileData, team_size: e.target.value })}><option>Seul</option><option>1-2</option><option>3-10</option><option>11+</option></select></div>

                <div className="flex items-center gap-2 mt-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <input
                    type="checkbox"
                    id="alertsToggle"
                    className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                    checked={profileData.alerts_enabled !== false} // Default true
                    onChange={e => setProfileData({ ...profileData, alerts_enabled: e.target.checked })}
                  />
                  <label htmlFor="alertsToggle" className="text-sm font-medium text-slate-800 cursor-pointer">
                    Recevoir les alertes quotidiennes par email
                  </label>
                </div>
                <div className="pt-6 border-t border-gray-100 flex flex-col gap-3">
                  <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Gestion des données (RGPD)</p>
                  <div className="flex gap-2">
                    <button onClick={handleDataExport} className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-bold hover:bg-gray-50 flex items-center justify-center gap-2">
                      <Download size={14} /> Export Données
                    </button>
                    <button onClick={deleteAccount} className="flex-1 px-3 py-2 border border-red-100 text-red-600 rounded-lg text-xs font-bold hover:bg-red-50 flex items-center justify-center gap-2">
                      <Trash2 size={14} /> Supprimer Compte
                    </button>
                  </div>
                </div>

                <button onClick={() => { updateProfile(); setShowProfileModal(false); }} className="w-full mt-4 bg-emerald-600 text-white py-3 rounded-lg font-bold hover:bg-emerald-700 flex justify-center items-center gap-2">
                  <Check className="w-5 h-5" /> Mettre à jour
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* CHAT IA */}
      {
        showAIChat && (
          <div className="fixed z-50 bg-white shadow-2xl border rounded-t-xl bottom-0 right-0 md:right-10 w-full md:w-96 h-[600px] flex flex-col">
            <div className="bg-slate-900 text-white p-3 flex justify-between items-center rounded-t-xl">
              <span className="font-bold flex gap-2"><Shield size={16} /> Assistant IA</span>
              <button onClick={() => setShowAIChat(false)} aria-label="Fermer l'assistant"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-hidden">
              <AIChat userId={user.id} API_URL={API_URL} />
            </div>
          </div>
        )
      }
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </div >
  );
}